import * as React from 'react';

import { Form, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import ActionButton from 'components/buttons/action-button/ActionButton';
import { ModalFrame, ModalFrameFooter, ModalFrameInner } from 'components/modal';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import { isRequired } from 'utils/validators';
import { __t, applyTranslations } from 'utils/language';
import useManagedInputValue from 'hooks/use-managed-input-value';

import './profile-rejection-reason.scss';

applyTranslations(require('./profile-rejection-reason.i18n.json'));
applyTranslations(require('locales/common/collabary-common.i18n.json'));

type Props = {
  onCloseModal: () => void,
  onRejection: (b: string, a: string) => void
};

const REJECTED_REASONS = [
  'We are looking for creators with at least 1000 followers. We hope to see you grow your following and join us in the future. All the best!',
  'We are looking for influencers with the majority of their followers from European countries. Feel free to reach out again in the future if this changes. We wish you all the best!',
  'It looks like you already have an active profile on Collabary. Please reach out to us if you\'re having log-in issues!',
  'Unfortunately, your audience does not meet our criteria at the moment. Please check out our guidelines. We wish you all the best!',
  'Unfortunately you don’t have enough postings. Feel free to reach out again in the future. We hope to see you join us soon!',
  'We have very strict guidelines concerning followership, picture language and engagement. Feel free to reach out again in the future.',
  'Unfortunately there is no IG channel added to your profile. Please add it to your profile when signing up :)',
  'Collabary is only operating in Europe, therefore I am afraid we cannot offer you access right now. All the best for the future :)',
  'Collabary only operates in Europe, so we are looking for influencers with the majority of their followers from within the European Union. We wish you all the best!',
  'Because your IG profile is set to private, we cannot accept your application. Please remember to change your privacy settings before signing up. We hope to see you join us soon!',
  'Influencer joined a new agency, therefore this profile is no longer active.',
  'Influencer left agency, therefore this profile is no longer active.',
  'Other'
];

const ProfileRejectionReason = (
  {
    onCloseModal,
    onRejection
  }: Props
): JSX.Element => {
  const [creatorMessage, setCreatorMessage] = useManagedInputValue('');
  const [creatorMessageOther, setCreatorMessageOther] = useManagedInputValue('');
  const [internalReason, setInternalReason] = useManagedInputValue('');

  return (
    <ModalFrame
      title={__t('profile_rejection_reason.modal_title')}
    >
      <Form
        onSubmit={() => onRejection(internalReason, creatorMessage === 'Other' ? creatorMessageOther : creatorMessage)}
      >
        <ModalFrameInner>
          <section className="profile-rejection-reason__section">
            <div className="profile-rejection-reason__title">
              {__t('profile_rejection_reason.message_to_creator_title')}
            </div>
            <div className="profile-rejection-reason__explanation">
              {__t('profile_rejection_reason.message_to_creator_explanation')}
            </div>
            <MixedSelectDropdown
              name="rejected-reason"
              items={REJECTED_REASONS.map(reason => ({
                identifier: reason,
                isDisabled: false,
                isSelected: reason === creatorMessage,
                label: reason,
                value: reason
              }))}
              withSelectedInList
              onItemsToggle={(items, __, item) => setCreatorMessage(item?.value || '')}
            />
            {
              creatorMessage === 'Other' && (
                <FormField
                  direction="column"
                  showValidationIcon={false}
                  label=""
                >
                  <Input
                    placeholder={__t('profile_rejection_reason.add_comment')}
                    width="responsive"
                    multiline
                    value={creatorMessageOther}
                    onChange={(event) => setCreatorMessageOther(event.target.value)}
                    className="profile-rejection-reason___reason"
                  />
                </FormField>
              )
            }
          </section>

          <section className="profile-rejection-reason__section">
            <div className="profile-rejection-reason__title profile-rejection-reason__title--required">
              {__t('profile_rejection_reason.internal_message_title')}
            </div>
            <div className="profile-rejection-reason__explanation">
              {__t('profile_rejection_reason.internal_message_explanation')}
            </div>
            <FormField
              direction="column"
              showValidationIcon={false}
              validators={[
                isRequired(internalReason)
              ]}
              label=""
            >
              <Input
                width="responsive"
                multiline
                value={internalReason}
                onChange={(event) => setInternalReason(event.target.value)}
              />
            </FormField>
          </section>
        </ModalFrameInner>

        <ModalFrameFooter>
          <ActionButton
            color="tertiary"
            onClick={onCloseModal}
          >
            {__t('collabary_common.action_button.cancel')}
          </ActionButton>
          <ActionButton
            type="submit"
          >
            {__t('profile_rejection_reason.submit_label')}
          </ActionButton>
        </ModalFrameFooter>
      </Form>
    </ModalFrame>
  );
};

export default ProfileRejectionReason;
